/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, PlusCircle, Building2, Tag, User, Hash, Box, FileEdit, FileText, Info, UserCheck
} from 'lucide-react';
import { serviceRequestService } from '../services/serviceRequestService';
import { accountService } from '../services/accountService';
import { subAccountService } from '../services/subAccountService';
import { userService } from '../services/userService';
import toastService from '../utils/toastService';
import { useAuth } from '../context/AuthContext';
import useServiceRequestTypes from '../hooks/useServiceRequestTypes';
import useServiceRequestTypeHierarchy from '../hooks/useServiceRequestTypeHierarchy';
import HierarchySelector from '../components/hierarchy/HierarchySelector';
import {
  findLatestTypeByCode,
  resolveAssetIdentifierLabel,
  resolveCustomerNameLabel,
  resolveEquipmentIdentifierLabel,
} from '../utils/serviceRequestLabels';
import { getLeafUid } from '../utils/serviceRequestHierarchy';
import { ACCOUNT_LABEL, SUB_ACCOUNT_LABEL, SERVICE_REQUEST_LABEL } from '../constants/entityLabels';

const SERVICE_REQUESTS_ROUTE = '/service-requests';

const sortByName = (list) =>
  [...list].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));

const NewServiceRequest = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [form, setForm] = useState({
    type: '',
    customerName: '',
    assetIdentifier: '',
    equipmentIdentifier: '',
    additionalDetails: '',
    accountUid: '',
    subAccountUid: '',
    assignedUserUid: '',
    hierarchySelection: {},
  });

  const [accounts, setAccounts] = useState([]);
  const [subAccounts, setSubAccounts] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const isPlatformScope = !user?.accountUID;
  const isSubAccountScope = !!user?.subAccountUID;
  const showAccountField = isPlatformScope;
  const showSubAccountField = !isSubAccountScope;
  const effectiveAccountId = form.accountUid || user?.accountUID || '';
  const effectiveSubAccountId = form.subAccountUid || user?.subAccountUID || '';
  const { types } = useServiceRequestTypes(form.subAccountUid || undefined);
  const customerNameLabel = resolveCustomerNameLabel(form.type, types);
  const assetIdentifierLabel = resolveAssetIdentifierLabel(form.type, types);
  const equipmentIdentifierLabel = resolveEquipmentIdentifierLabel(form.type, types);
  const selectedTypeUid = form.type ? findLatestTypeByCode(form.type, types)?.uid : undefined;
  const { levels: hierarchyLevels, nodes: hierarchyNodes, loading: hierarchyLoading } =
    useServiceRequestTypeHierarchy(selectedTypeUid);

  useEffect(() => {
    if (isPlatformScope) {
      const fetchAccounts = async () => {
        try {
          const { data } = await accountService.getAccounts({ limit: 100 });
          setAccounts(sortByName(data.data || data || []));
        } catch {
          toastService.error(`Failed to load ${ACCOUNT_LABEL.pluralLower}`);
        }
      };
      fetchAccounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlatformScope]);

  useEffect(() => {
    if (isSubAccountScope || !effectiveAccountId) { return; }
    const fetchSubAccounts = async () => {
      try {
        const { data } = await subAccountService.getSubAccounts(effectiveAccountId, { limit: 100 });
        setSubAccounts(sortByName(data.data || []));
      } catch {
        toastService.error(`Failed to load ${SUB_ACCOUNT_LABEL.pluralLower}`);
      }
    };
    fetchSubAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveAccountId, isSubAccountScope]);

  useEffect(() => {
    setForm((f) => ({ ...f, subAccountUid: '' }));
    setSubAccounts([]);
  }, [form.accountUid]);

  useEffect(() => {
    setForm((f) => ({ ...f, assignedUserUid: '' }));
    if (!effectiveSubAccountId) { setAssignableUsers([]); return; }
    const fetchAssignableUsers = async () => {
      try {
        const { data } = await userService.getUsers({ subAccountUid: effectiveSubAccountId, status: 'active', limit: 100 });
        setAssignableUsers(data.users || []);
      } catch {
        toastService.error('Failed to load assignable users');
      }
    };
    fetchAssignableUsers();
  }, [effectiveSubAccountId]);

  // Auto-select the type when only one is available; drop a selection the
  // newly loaded list (e.g. after a sub-account change) no longer offers.
  useEffect(() => {
    if (types.length === 1) {
      const onlyCode = types[0].code;
      if (form.type !== onlyCode) {
        setForm(f => ({ ...f, type: onlyCode, hierarchySelection: {} }));
        setFieldErrors(p => ({ ...p, type: null }));
      }
    } else if (form.type && !types.some(t => t.code === form.type)) {
      setForm(f => ({ ...f, type: '', hierarchySelection: {} }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [types]);

  const validateForm = () => {
    const errors = {};
    if (showAccountField && !form.accountUid) { errors.accountUid = `${ACCOUNT_LABEL.singular} is required`; }
    if (showSubAccountField && !form.subAccountUid) { errors.subAccountUid = `${SUB_ACCOUNT_LABEL.singular} is required`; }
    if (!form.type) { errors.type = 'Type is required'; }
    if (!form.customerName.trim()) { errors.customerName = `${customerNameLabel} is required`; }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleBlur = (field, value) => {
    if (field === 'customerName' && !value.trim()) {
      setFieldErrors(prev => ({ ...prev, customerName: `${customerNameLabel} is required` }));
    } else if (field === 'type' && !value) {
      setFieldErrors(prev => ({ ...prev, type: 'Type is required' }));
    } else {
      setFieldErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (hierarchyLoading || !validateForm()) { return; }
    try {
      setSubmitting(true);
      const payload = {
        type: form.type,
        hierarchyNodeUid: getLeafUid(form.hierarchySelection) || undefined,
        customerName: form.customerName.trim(),
        assetIdentifier: form.assetIdentifier.trim() || undefined,
        equipmentIdentifier: form.equipmentIdentifier.trim() || undefined,
        additionalDetails: form.additionalDetails.trim() || undefined,
        assignedUserUid: form.assignedUserUid || undefined,
        ...(showAccountField && { accountUid: form.accountUid }),
        ...(showSubAccountField && { subAccountUid: form.subAccountUid }),
      };
      await serviceRequestService.createServiceRequest(payload);
      toastService.success(`${SERVICE_REQUEST_LABEL.singular} created successfully`);
      navigate(SERVICE_REQUESTS_ROUTE);
    } catch (err) {
      const msg = err.response?.data?.message;
      toastService.error(Array.isArray(msg) ? msg[0] : (msg || `Failed to create ${SERVICE_REQUEST_LABEL.singularLower}`));
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `w-full px-4 py-2.5 bg-base-100 border rounded-xl text-sm text-base-content placeholder:text-base-content/30 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all ${fieldErrors[field] ? 'border-error' : 'border-base-300'}`;

  const selectClassFor = (hasError) =>
    `w-full px-4 py-2.5 bg-base-100 border rounded-xl text-sm text-base-content focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all disabled:bg-base-200/50 disabled:text-base-content/40 ${hasError ? 'border-error' : 'border-base-300'}`;

  const selectClass = (field) => selectClassFor(!!fieldErrors[field]);

  return (
    <div className="space-y-6 p-1 sm:p-2 w-full pb-12">
      {/* Top Breadcrumb */}
      <div>
        <button
          type="button"
          onClick={() => navigate(SERVICE_REQUESTS_ROUTE)}
          className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-base-content/50 hover:text-primary transition-colors group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          Back to {SERVICE_REQUEST_LABEL.plural}
        </button>
      </div>

      {/* Hero Header Banner */}
      <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black uppercase tracking-widest text-base-content/40">
                New Record
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-base-content tracking-tight">
              Create {SERVICE_REQUEST_LABEL.singular}
            </h1>
            <p className="text-xs text-base-content/50">
              Provide {SERVICE_REQUEST_LABEL.singularLower} details, customer information, and identifier metadata.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Scope & Organization (when applicable) */}
        {(showAccountField || showSubAccountField) && (
          <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
            <div className="flex items-center gap-2 pb-3 border-b border-base-content/10">
              <Building2 size={18} className="text-primary" />
              <h2 className="text-base font-black text-base-content uppercase tracking-wider">
                Organization & Scope
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {showAccountField && (
                <div className="space-y-1.5">
                  <label htmlFor="accountUid" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block">
                    {ACCOUNT_LABEL.singular} *
                  </label>
                  <select
                    id="accountUid"
                    className={selectClass('accountUid')}
                    value={form.accountUid}
                    onBlur={e => handleBlur('accountUid', e.target.value)}
                    onChange={e => { setForm(f => ({ ...f, accountUid: e.target.value })); setFieldErrors(p => ({ ...p, accountUid: null })); }}
                  >
                    <option value="">— Select {ACCOUNT_LABEL.withArticle} —</option>
                    {accounts.map(acc => (
                      <option key={acc.id} value={acc.id}>{acc.name}</option>
                    ))}
                  </select>
                  {fieldErrors.accountUid && <p className="text-error text-xs font-medium">{fieldErrors.accountUid}</p>}
                </div>
              )}

              {showSubAccountField && (
                <div className="space-y-1.5">
                  <label htmlFor="subAccountUid" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest block">
                    {SUB_ACCOUNT_LABEL.singular} *
                  </label>
                  <select
                    id="subAccountUid"
                    className={selectClass('subAccountUid')}
                    value={form.subAccountUid}
                    disabled={showAccountField && !form.accountUid}
                    onBlur={e => handleBlur('subAccountUid', e.target.value)}
                    onChange={e => { setForm(f => ({ ...f, subAccountUid: e.target.value })); setFieldErrors(p => ({ ...p, subAccountUid: null })); }}
                  >
                    <option value="">— Select {SUB_ACCOUNT_LABEL.withArticle} —</option>
                    {subAccounts.map(sub => (
                      <option key={sub.id} value={sub.id}>{sub.name}</option>
                    ))}
                  </select>
                  {fieldErrors.subAccountUid && <p className="text-error text-xs font-medium">{fieldErrors.subAccountUid}</p>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Request Details — type, its hierarchy, identifiers */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-base-content/10">
            <FileText size={18} className="text-primary" />
            <h2 className="text-base font-black text-base-content uppercase tracking-wider">
              {SERVICE_REQUEST_LABEL.singular} Details
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Type */}
            <div className="space-y-1.5">
              <label htmlFor="type" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
                <Tag size={12} className="text-primary" />
                <span>Type *</span>
              </label>
              <select
                id="type"
                className={selectClass('type')}
                value={form.type}
                disabled={types.length === 0}
                onBlur={e => handleBlur('type', e.target.value)}
                onChange={e => { setForm(f => ({ ...f, type: e.target.value, hierarchySelection: {} })); setFieldErrors(p => ({ ...p, type: null })); }}
              >
                {types.length !== 1 && <option value="">— Select a type —</option>}
                {types.map(t => (
                  <option key={t.uid} value={t.code}>{t.name}</option>
                ))}
              </select>
              {fieldErrors.type && <p className="text-error text-xs font-medium">{fieldErrors.type}</p>}
              {types.length === 0 && (
                <p className="text-base-content/40 text-xs font-medium flex items-center gap-1 mt-1">
                  <Info size={12} />
                  No {SERVICE_REQUEST_LABEL.singularLower} types configured — contact an administrator.
                </p>
              )}
            </div>

            {/* Hierarchy — the type's levels (e.g. Category > Asset > Manufacturer), when configured */}
            <HierarchySelector
              inline
              levels={hierarchyLevels}
              nodes={hierarchyNodes}
              value={form.hierarchySelection}
              loading={hierarchyLoading}
              selectClassName={selectClassFor}
              onChange={(hierarchySelection) => setForm(f => ({ ...f, hierarchySelection }))}
            />

            {/* Asset Identifier */}
            <div className="space-y-1.5">
              <label htmlFor="assetIdentifier" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
                <Hash size={12} className="text-primary" />
                <span>{assetIdentifierLabel}</span>
              </label>
              <input
                id="assetIdentifier"
                type="text"
                maxLength={255}
                placeholder={`Enter ${assetIdentifierLabel.toLowerCase()}`}
                className={inputClass('assetIdentifier')}
                value={form.assetIdentifier}
                onChange={e => setForm(f => ({ ...f, assetIdentifier: e.target.value }))}
              />
            </div>

            {/* Equipment Identifier */}
            <div className="space-y-1.5">
              <label htmlFor="equipmentIdentifier" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
                <Box size={12} className="text-primary" />
                <span>{equipmentIdentifierLabel}</span>
              </label>
              <input
                id="equipmentIdentifier"
                type="text"
                maxLength={255}
                placeholder={`Enter ${equipmentIdentifierLabel.toLowerCase()}`}
                className={inputClass('equipmentIdentifier')}
                value={form.equipmentIdentifier}
                onChange={e => setForm(f => ({ ...f, equipmentIdentifier: e.target.value }))}
              />
            </div>
          </div>
        </div>

        {/* Additional Details */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-base-content/10">
            <div className="flex items-center gap-2">
              <FileEdit size={18} className="text-primary" />
              <h2 className="text-base font-black text-base-content uppercase tracking-wider">
                Additional Details
              </h2>
            </div>
            <span className="text-[10px] text-base-content/40 font-mono">Optional</span>
          </div>

          <div className="space-y-1.5">
            <textarea
              id="additionalDetails"
              rows={4}
              maxLength={4000}
              placeholder={`Enter any additional notes, instructions, or specific context for this ${SERVICE_REQUEST_LABEL.singularLower}...`}
              className="w-full px-4 py-3 bg-base-100 border border-base-300 rounded-xl text-sm text-base-content placeholder:text-base-content/30 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all resize-y"
              value={form.additionalDetails}
              onChange={e => setForm(f => ({ ...f, additionalDetails: e.target.value }))}
            />
            <div className="flex justify-end">
              <span className="text-[10px] text-base-content/40 font-medium">
                {form.additionalDetails.length} / 4000
              </span>
            </div>
          </div>
        </div>

        {/* Customer & Assignment */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-base-content/10">
            <UserCheck size={18} className="text-primary" />
            <h2 className="text-base font-black text-base-content uppercase tracking-wider">
              {customerNameLabel} & Assignment
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Customer Name */}
            <div className="space-y-1.5">
              <label htmlFor="customerName" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
                <User size={12} className="text-primary" />
                <span>{customerNameLabel} *</span>
              </label>
              <input
                id="customerName"
                type="text"
                maxLength={255}
                placeholder={`Enter ${customerNameLabel.toLowerCase()}`}
                className={inputClass('customerName')}
                value={form.customerName}
                onBlur={e => handleBlur('customerName', e.target.value)}
                onChange={e => { setForm(f => ({ ...f, customerName: e.target.value })); setFieldErrors(p => ({ ...p, customerName: null })); }}
              />
              {fieldErrors.customerName && <p className="text-error text-xs font-medium">{fieldErrors.customerName}</p>}
            </div>

            {/* Assigned To */}
            <div className="space-y-1.5">
              <label htmlFor="assignedUserUid" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
                <UserCheck size={12} className="text-primary" />
                <span>Assign To</span>
              </label>
              <select
                id="assignedUserUid"
                className={selectClass('assignedUserUid')}
                value={form.assignedUserUid}
                disabled={!effectiveSubAccountId}
                onChange={e => setForm(f => ({ ...f, assignedUserUid: e.target.value }))}
              >
                <option value="">— Unassigned —</option>
                {assignableUsers.map(u => (
                  <option key={u.userId} value={u.userId}>{u.displayName}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate(SERVICE_REQUESTS_ROUTE)}
            className="w-full sm:w-auto px-6 py-3 border border-base-content/20 text-base-content/70 hover:bg-base-200 rounded-xl font-bold text-sm transition-all"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || hierarchyLoading}
            className="w-full sm:flex-1 btn-premium justify-center"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-primary-content/30 border-t-primary-content rounded-full animate-spin"></div>
                <span>Creating {SERVICE_REQUEST_LABEL.singular}...</span>
              </>
            ) : (
              <>
                <PlusCircle size={16} />
                <span>Create {SERVICE_REQUEST_LABEL.singular}</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewServiceRequest;

